import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

import { useAsyncRequest } from 'hooks';
import { getObjectsDifference } from 'utils/object';

import {
  getInitialAnswers,
  shouldSkipQuestionProgress,
  getStepsFromQuestions,
} from './utils';
import validate, { isAnswered as isQuestionAnswered } from './validate';

export default function useTest(schema, schemaOptions = {}) {
  const optionsRef = useRef(schemaOptions);
  const schemaRef = useRef(
    (() => {
      if (schema.onInit) {
        return schema.onInit(optionsRef.current.initOptions);
      }
      return schema;
    })()
  );
  const [isOver, setIsOver] = useState(false);
  const [passedCount, setPassedCount] = useState(0);
  const [questions, setQuestions] = useState(
    Array.isArray(schemaRef.current.questions)
      ? schemaRef.current.questions.map((q) => ({
        type: schemaRef.current.questionOptions?.type,
        ...q,
        group: 'group1',
        enabled: true,
      }))
      : [
        ...Object.entries(schemaRef.current.questions)
          .map(([group, qts]) =>
            qts.map((q) => ({
              type: schemaRef.current.questionOptions?.type,
              ...q,
              group,
              enabled: true,
            }))
          )
          .flat(2),
      ]
  );
  const activeQuestions = useMemo(
    () => questions.filter((q) => q.enabled),
    [questions]
  );
  const [steps, setSteps] = useState(() =>
    getStepsFromQuestions(activeQuestions, {
      randomize: schemaRef.current.meta?.randomize,
    })
  );
  const activeQuestionsRef = useRef(activeQuestions);
  useEffect(() => {
    activeQuestionsRef.current = activeQuestions;
  }, [activeQuestions]);
  useEffect(() => {
    setSteps(getStepsFromQuestions(activeQuestions));
  }, [activeQuestions]);

  useEffect(() => {
    if (passedCount > 0) {
      setSteps(
        getStepsFromQuestions(activeQuestionsRef.current, {
          randomize: schemaRef.current.meta?.randomize,
          test: true,
        })
      );
    }
  }, [passedCount]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const {
    errorMessage,
    isLoading,
    invoke: invokeAsyncRequest,
  } = useAsyncRequest();
  const currentQuestions = useMemo(
    () => activeQuestions.filter((q) => q.group === steps[currentStepIndex]),
    [steps, currentStepIndex, activeQuestions]
  );

  const indexedQuestions = useMemo(
    () =>
      questions.reduce((acc, q) => {
        acc[q.id] = q;
        return acc;
      }, {}),
    [questions]
  );
  const [answers, setAnswers] = useState(getInitialAnswers(questions));
  const answersRef = useRef(answers);
  const progress = useMemo(() => {
    const questionsToAnswer = questions.filter(
      (q) => !shouldSkipQuestionProgress(q)
    );
    const passedQuestions = questionsToAnswer.filter((q) =>
      isQuestionAnswered(indexedQuestions[q.id], answers[q.id])
    );

    return Math.ceil((passedQuestions.length / questionsToAnswer.length) * 100) || 0;
  }, [questions, indexedQuestions, answers]);
  const toggleQuestionsBy = useCallback(
    (field = 'id', value, enabled = true) => {
      setQuestions((oldQuestions) =>
        oldQuestions.map((q) => {
          const questionValue = q[field];
          const isQuestionFit = Array.isArray(value)
            ? value.includes(questionValue)
            : questionValue === value;
          if (isQuestionFit) {
            return {
              ...q,
              enabled,
            };
          }
          return q;
        })
      );
    },
    []
  );
  const enableQuestionsBy = useCallback(
    (field = 'id', value) => toggleQuestionsBy(field, value, true),
    [toggleQuestionsBy]
  );
  const disableQuestionsBy = useCallback(
    (field = 'id', value) => toggleQuestionsBy(field, value, false),
    [toggleQuestionsBy]
  );

  const setAnswer = useCallback((questionEvent) => {
    if (!questionEvent || !questionEvent.name || questionEvent.value == null) {
      return false;
    }
    const { name, value } = questionEvent;
    setAnswers((currentAnswers) => {
      if (currentAnswers[name] === value) {
        return currentAnswers;
      }
      return {
        ...currentAnswers,
        [name]: value,
      };
    });
    return true;
  }, []);
  const restart = useCallback(() => {
    const oldAnswers = answersRef.current;
    setIsOver(false);
    setAnswers({});
    setCurrentStepIndex(0);
    setPassedCount((currentPassedCount) => currentPassedCount + 1);
    if (schemaRef.current.onRestart) {
      schemaRef.current.onRestart({ answers: oldAnswers, setAnswer });
    }
  }, [setAnswer]);
  const nextStep = useCallback(() => {
    const isValid = validate(currentQuestions, answers);
    if (!isValid) {
      return false;
    }
    if (schemaRef.current.onBeforeNextStep) {
      schemaRef.current.onBeforeNextStep({
        answers,
        currentStepIndex,
        steps,
        setQuestions,
        disableQuestionsBy,
      });
    }
    setCurrentStepIndex(Math.min(steps.length - 1, currentStepIndex + 1));
    return true;
  }, [steps, currentStepIndex, currentQuestions, disableQuestionsBy, answers]);

  const submitWrapper = useCallback(
    () =>
      invokeAsyncRequest(async () => {
        const isValid = validate(currentQuestions, answers);
        if (!isValid) {
          return 'validation';
        }
        const {
          token,
          schemaName,
          shouldSkipResult,
          teamId,
          shouldSaveResult,
          submit,
        } = optionsRef.current;
        const skipResult =
          typeof shouldSkipResult === 'function'
            ? await shouldSkipResult()
            : shouldSkipResult;

        setIsOver(true);
        if (skipResult) {
          return 'skip';
        }
        if (submit) {
          const results = await submit({
            schemaName,
            answers,
            token: `${token || ''}`,
            teamId,
            shouldSaveResult,
            additionalSchemas: schemaRef.current.additionalSchemas,
          });
          return results;
        }
        return null;
      }),
    [answers, currentQuestions, invokeAsyncRequest]
  );
  // fill skipped questions with default value in a passed group
  useEffect(() => {
    setAnswers((currentAnswers) => {
      const answersToFill = {};
      const currentGroupQuestions = activeQuestionsRef.current.filter(
        (q) => q.group === steps[currentStepIndex]
      );
      const passedGroupQuestions = activeQuestionsRef.current.filter(
        (q) => q.group === steps[currentStepIndex - 1]
      );
      if (passedGroupQuestions?.length) {
        passedGroupQuestions.forEach((q) => {
          if (currentAnswers[q.id] == null) {
            answersToFill[q.id] = 0;
          }
        });
      }
      if (currentGroupQuestions?.length) {
        currentGroupQuestions.forEach((q) => {
          if (q.type === 'question') {
            answersToFill[q.id] = 0;
          }
        });
      }
      if (Object.keys(answersToFill).length) {
        return {
          ...currentAnswers,
          ...answersToFill,
        };
      }
      return currentAnswers;
    });
  }, [currentStepIndex, steps]);

  useEffect(() => {
    const questionsWithSourceOptions = questions.filter(
      (q) => q.type === 'order' && q.options
    );
    if (questionsWithSourceOptions.length) {
      questionsWithSourceOptions.forEach((question) => {
        const valuesFromSourceQuestion = question.options.reduce(
          (acc, option) => {
            if (Array.isArray(answers[option])) {
              return [...acc, ...answers[option]];
            }
            return acc;
          },
          []
        );
        let newAnswers = valuesFromSourceQuestion;
        const qAnswers = answers[question.id];
        if (qAnswers) {
          newAnswers = qAnswers.filter((answer) =>
            valuesFromSourceQuestion.includes(answer)
          );
          valuesFromSourceQuestion.forEach((v) => {
            if (!newAnswers.includes(v)) {
              newAnswers.push(v);
            }
          });
        }
        if (newAnswers && qAnswers?.length !== newAnswers?.length) {
          setAnswer({
            name: question.id,
            value: newAnswers,
          });
        }
      });
    }
  }, [answers, questions, setAnswer]);

  const hookTools = useMemo(
    () => ({
      questions: currentQuestions || [],
      allQuestions: questions || [],
      isLastStep: currentStepIndex === steps.length - 1,
      answers,
      progress,
      error: errorMessage,
      isLoading,
      isOver,
      stepKey: steps[currentStepIndex],
      nextStep,
      setQuestions,
      setAnswer,
      restart,
      submit: submitWrapper,
      enableQuestionsBy,
      disableQuestionsBy,
    }),
    [
      currentQuestions,
      questions,
      currentStepIndex,
      answers,
      progress,
      errorMessage,
      steps,
      isLoading,
      isOver,
      nextStep,
      setAnswer,
      restart,
      submitWrapper,
      enableQuestionsBy,
      disableQuestionsBy,
    ]
  );
  const hookToolsRef = useRef(hookTools);
  useEffect(() => {
    hookToolsRef.current = hookTools;
  }, [hookTools]);
  useEffect(() => {
    schemaRef.current.onChange?.({
      ...hookToolsRef.current,
      diff: getObjectsDifference(answers, answersRef.current),
    });
    answersRef.current = answers;
  }, [answers]);
  return hookTools;
}
